import { rgba } from 'polished';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';
import { useDebounce } from 'usehooks-ts';

import { CheckmarkIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { GeometryObserver } from './Popover';
import { Button } from './button/Button';

// Popover/Modal portals to document.body at z-index 3. WorkingArea used to
// render through Portal.Provider inside #root, so editors (category title,
// new question) sat behind the feedback-library modal.
export const WORKING_AREA_Z_INDEX = 4;
export const WORKING_AREA_MENU_Z_INDEX = WORKING_AREA_Z_INDEX + 1;

const fade = keyframes`
    0% { opacity: 0; };
    100% { opacity: 1; };
`;

const fadeOut = keyframes`
    0% { opacity: 1; };
    100% { opacity: 0; };
`;

const slide = keyframes`
    0% { transform: translateY(-100%); opacity: 0; }
    100% { transform: none; opacity: 1; }
`;

const slideOut = keyframes`
    0% { transform: none; opacity: 1; }
    100% { transform: translateY(-100%); opacity: 0; }
`;

const Phantom = styled(Box)<{ visible?: boolean }>`
	opacity: ${(props) => (props.visible ? 1 : 0)};
	pointer-events: ${(props) => (props.visible ? 'auto' : 'none')};
	transition: 0.9s
		${(props) => (props.visible ? `cubic-bezier(1, 0, 0, 1)` : `cubic-bezier(0, 1, 0, 1)`)} opacity;
`;

const Area = styled(Box)<{ left: number; top: number; width?: number; shift: number }>`
	position: fixed;
	top: ${(props) => props.top}px;
	left: ${(props) => props.left}px;
	${(props) => props.width !== undefined && `width: ${props.width}px;`}
	transform: translateY(${(props) => props.shift}px);
	transition: 1s cubic-bezier(0, 1, 0, 1) transform;
`;

const Contents = styled(Box)`
	box-shadow: 0px 8px 16px ${rgba(tokens.text.secondary, 0.25)};
	position: relative;
	z-index: 1;
	border-radius: 8px;
`;

const Toolbar = styled(Flex)<{ out?: boolean }>`
	animation: 1s cubic-bezier(0, 1, 0, 1) ${(props) => (props.out ? slideOut : slide)};
	animation-fill-mode: forwards;
`;

const Overlay = styled(Box)`
	background: ${rgba(tokens.text.secondary, 0.25)};
	position: fixed;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;
	pointer-events: auto;
`;

const Fade = styled(Box)<{ out?: boolean }>`
	animation: 1s
		${({ out }) =>
			out ? css`cubic-bezier(1, 0, 0, 1) ${fadeOut}` : css`cubic-bezier(0, 1, 0, 1) ${fade}`};
	animation-fill-mode: forwards;
	z-index: ${WORKING_AREA_Z_INDEX};
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;

	& > * {
		pointer-events: auto;
	}
`;

export function WorkingArea(props: {
	flexible?: boolean;
	active?: boolean;
	children: (active: boolean) => ReactNode;
	onDismiss?: () => void;
	onCancel?: () => void;
	onSave?: () => Promise<void> | void;
}) {
	const { t } = useTranslation();

	const ref = useRef<HTMLDivElement>(null);
	const areaRef = useRef<HTMLDivElement>(null);

	const [loading, setLoading] = useState(false);
	const [immediate, setImmediate] = useState(props.active);

	const active = useDebounce(props.active, 750);

	useEffect(() => {
		setTimeout(() => setImmediate(props.active), 10);
	}, [props.active]);

	return (
		<>
			<Phantom
				ref={ref}
				flexGrow={props.flexible ? 0 : 1}
				flexShrink={props.flexible ? 0 : 1}
				visible={!immediate}
			>
				{props.children(false)}
			</Phantom>
			{(props.active || active) &&
				typeof document !== 'undefined' &&
				createPortal(
					<Fade out={!props.active}>
						<Overlay onClick={props.onDismiss} />
						<GeometryObserver innerRef={ref}>
							{({ top, left, width }) => {
								return (
									<Area
										ref={areaRef}
										top={top}
										shift={
											immediate
												? Math.min(
														top,
														window.innerHeight - (areaRef.current?.clientHeight ?? 0) - 50,
													) - top
												: 0
										}
										left={left}
										width={props.flexible ? undefined : width}
									>
										<Contents>{props.children(true)}</Contents>
										{(props.onCancel || props.onSave) && (
											<Toolbar mt={2} out={!props.active}>
												<Button
													type="secondary"
													small
													title={t('common.cancel')}
													mr={2}
													onClick={props.onCancel}
												/>
												<Button
													small
													icon={<CheckmarkIcon />}
													title={t('common.save')}
													loading={loading}
													onClick={async () => {
														setLoading(true);
														await props.onSave?.();
														setLoading(false);
													}}
												/>
											</Toolbar>
										)}
									</Area>
								);
							}}
						</GeometryObserver>
					</Fade>,
					document.body,
				)}
		</>
	);
}
